import { useState, useCallback, useMemo } from 'react';
import { TrendingUp } from 'lucide-react';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Legend,
  Tooltip as ChartTooltip, ResponsiveContainer, Cell,
} from 'recharts';
import { analyticsApi } from '@/api/analytics.api';
import { usePermission } from '@/hooks/usePermission';
import { useAuth } from '@/hooks/useAuth';
import { Permissions } from '@/constants/permissions';
import { Spinner } from '@/components/ui/Spinner';
import { cn } from '@/utils/cn';
import { ReportToolbar, RunReportButton } from '@/components/reports/ReportToolbar';
import { LocationSelect } from '@/components/reports/LocationSelect';
import { CostCentreSelect } from '@/components/reports/CostCentreSelect';
import { ReportPrintPreview } from '@/components/reports/ReportPrintPreview';
import { ReportHeader } from '@/components/master/report/ReportHeader';
import { ReportFooter } from '@/components/master/report/ReportFooter';
import { exportToExcel } from '@/utils/reportExport';
import type { ExcelColumn } from '@/utils/reportExport';
import {
  INPUT_CLS, LABEL_CLS, TH_CLS, TH_R_CLS, AMT_CLS, CARD_CLS,
  today, firstOfMonth, fmtDate, fmtAmt, fmtAmtZero, fmtPct,
  Delta, Section, EmptyState, CHART_COLOURS,
} from '@/components/analytics/AnalyticsKit';
import type {
  AnalyticsGroupBy, ProfitabilityDto, ProfitabilityFilter, ProfitabilityRow,
} from '@/types/analytics.types';

// ── Options ───────────────────────────────────────────────────────────────────

const GROUP_BY_OPTIONS: Array<{ value: AnalyticsGroupBy; label: string }> = [
  { value: 'COST_CENTRE', label: 'Cost Centre' },
  { value: 'LOCATION', label: 'Branch / Location' },
  { value: 'ACCOUNT', label: 'Account' },
];

const GROUP_BY_LABELS: Record<AnalyticsGroupBy, string> = {
  COST_CENTRE: 'Cost Centre',
  LOCATION: 'Branch / Location',
  ACCOUNT: 'Account',
  CATEGORY: 'Category',
};

const TOTAL_BAR_COLOUR = '#6366f1';
const POSITIVE_BAR_COLOUR = '#10b981';
const NEGATIVE_BAR_COLOUR = '#ef4444';

// ── Chart tooltip ─────────────────────────────────────────────────────────────

const AmountTooltip = ({ active, payload, label }: {
  active?: boolean;
  payload?: Array<{ name: string; value: number; color: string }>;
  label?: string;
}) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-3 text-xs">
      <p className="font-semibold text-gray-700 dark:text-gray-200 mb-2">{label}</p>
      {payload.map(p => (
        <div key={p.name} className="flex items-center justify-between gap-6">
          <span className="flex items-center gap-1.5" style={{ color: p.color }}>
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: p.color }} />
            {p.name}
          </span>
          <span className="font-medium text-gray-900 dark:text-white tabular-nums">
            {fmtAmtZero(p.value)}
          </span>
        </div>
      ))}
    </div>
  );
};

// ── Excel export ──────────────────────────────────────────────────────────────

const EXCEL_COLUMNS: ExcelColumn[] = [
  { key: 'Code', header: 'Code', width: 16 },
  { key: 'Name', header: 'Name', width: 34 },
  { key: 'Revenue', header: 'Revenue', width: 16, numFmt: '#,##0.00' },
  { key: 'Cost of Sales', header: 'Cost of Sales', width: 16, numFmt: '#,##0.00' },
  { key: 'Gross Profit', header: 'Gross Profit', width: 16, numFmt: '#,##0.00' },
  { key: 'GM%', header: 'GM %', width: 10 },
  { key: 'Operating Expenses', header: 'Operating Expenses', width: 18, numFmt: '#,##0.00' },
  { key: 'Operating Profit', header: 'Operating Profit', width: 17, numFmt: '#,##0.00' },
  { key: 'Net Profit', header: 'Net Profit', width: 16, numFmt: '#,##0.00' },
  { key: 'NM%', header: 'NM %', width: 10 },
  { key: 'Previous Net Profit', header: 'Previous Net Profit', width: 18, numFmt: '#,##0.00' },
  { key: 'Growth%', header: 'Growth %', width: 12 },
  { key: 'Contribution%', header: 'Contribution %', width: 14 },
];

function toExcelRow(r: ProfitabilityRow, nameOverride?: string) {
  return {
    Code: nameOverride ? '' : r.groupCode,
    Name: nameOverride ?? r.groupName,
    Revenue: r.revenue,
    'Cost of Sales': r.costOfSales,
    'Gross Profit': r.grossProfit,
    'GM%': r.grossMarginPercent,
    'Operating Expenses': r.operatingExpenses,
    'Operating Profit': r.operatingProfit,
    'Net Profit': r.netProfit,
    'NM%': r.netMarginPercent,
    'Previous Net Profit': r.previousNetProfit,
    'Growth%': r.growthPercent ?? '',
    'Contribution%': r.contributionPercent,
  };
}

// ── Component ─────────────────────────────────────────────────────────────────

export const ProfitabilityPage = () => {
  const { hasPermission } = usePermission();
  const { user } = useAuth();

  const [filter, setFilter] = useState<ProfitabilityFilter>({
    fromDate: firstOfMonth(),
    toDate: today(),
    groupBy: 'COST_CENTRE',
  });
  const [data, setData] = useState<ProfitabilityDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPrint, setShowPrint] = useState(false);

  const run = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await analyticsApi.getProfitability(filter);
      setData(res.data?.data ?? null);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  // Top contributors by net profit — computed before the permission early-return is irrelevant
  // because hooks must run unconditionally; the guard below returns after all hooks.
  const contributionData = useMemo(() => {
    if (!data) return [];
    return [...data.rows]
      .sort((a, b) => b.netProfit - a.netProfit)
      .slice(0, 10)
      .map(r => ({ name: r.groupName, netProfit: r.netProfit }));
  }, [data]);

  if (!hasPermission(Permissions.RPT_PROFITABILITY)) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400">
        You do not have permission to view this report.
      </div>
    );
  }

  const groupLabel = data ? GROUP_BY_LABELS[data.groupBy] : 'Group';

  const handleExcel = () => {
    if (!data) return;
    const rows = data.rows.map(r => toExcelRow(r));
    rows.push(toExcelRow(data.total, 'TOTAL'));
    exportToExcel({
      sheetName: 'Profitability',
      columns: EXCEL_COLUMNS,
      rows,
      title: 'Profitability Analysis',
      subtitle: `${data.companyName} · ${GROUP_BY_LABELS[data.groupBy]} · ${filter.fromDate} to ${filter.toDate}`,
    }, `Profitability_${filter.fromDate}_${filter.toDate}`);
  };

  return (
    <div className="flex flex-col h-full gap-4 p-4 overflow-auto">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <TrendingUp size={22} className="text-brand-500" />
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">Profitability Analysis</h1>
        </div>
        <ReportToolbar
          onExcelExport={handleExcel}
          onPrint={() => setShowPrint(true)}
          hasData={!!data}
        />
      </div>

      {/* Filter panel */}
      <div className={cn(CARD_CLS, 'p-4 space-y-3')}>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <div>
            <label className={LABEL_CLS}>From Date *</label>
            <input
              type="date"
              value={filter.fromDate}
              onChange={e => setFilter(f => ({ ...f, fromDate: e.target.value }))}
              className={INPUT_CLS}
            />
          </div>
          <div>
            <label className={LABEL_CLS}>To Date *</label>
            <input
              type="date"
              value={filter.toDate}
              onChange={e => setFilter(f => ({ ...f, toDate: e.target.value }))}
              className={INPUT_CLS}
            />
          </div>
          <div>
            <label className={LABEL_CLS}>Group By</label>
            <select
              value={filter.groupBy ?? 'COST_CENTRE'}
              onChange={e => setFilter(f => ({ ...f, groupBy: e.target.value as AnalyticsGroupBy }))}
              className={INPUT_CLS}
            >
              {GROUP_BY_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className={LABEL_CLS}>Location</label>
            <LocationSelect
              value={filter.locationId}
              onChange={id => setFilter(f => ({ ...f, locationId: id || undefined }))}
            />
          </div>
          <div>
            <label className={LABEL_CLS}>Cost Centre</label>
            <CostCentreSelect
              value={filter.costCenterId}
              onChange={id => setFilter(f => ({ ...f, costCenterId: id || undefined }))}
            />
          </div>
        </div>

        <div className="flex justify-end">
          <RunReportButton
            onClick={() => { void run(); }}
            isLoading={loading}
            disabled={!filter.fromDate || !filter.toDate}
          />
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-700 dark:text-red-400">
          {error}
        </div>
      )}

      {/* Loading */}
      {loading && !data && (
        <div className="flex-1 flex items-center justify-center">
          <Spinner size="lg" />
        </div>
      )}

      {/* Empty */}
      {!data && !loading && (
        <EmptyState message='Set the date range and click "Run Report" to generate the Profitability Analysis.' />
      )}

      {/* Results */}
      {data && (
        <div className="flex flex-col gap-4">
          {/* Table */}
          <div className={cn(CARD_CLS, 'flex flex-col min-h-0')}>
            <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
              <div className="text-sm font-semibold text-gray-900 dark:text-white">{data.companyName}</div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                Profitability by {groupLabel} — {fmtDate(data.fromDate)} to {fmtDate(data.toDate)}
                {' · '}vs {fmtDate(data.previousFromDate)} – {fmtDate(data.previousToDate)}
                {' · '}{data.rows.length} rows
                {' · '}Generated {fmtDate(data.generatedAt)}
              </div>
            </div>

            <div className="overflow-auto">
              <table className="w-full text-sm border-collapse min-w-[1300px]">
                <thead className="sticky top-0 bg-gray-50 dark:bg-gray-800 z-10">
                  <tr>
                    <th className={cn(TH_CLS, 'w-28')}>Code</th>
                    <th className={TH_CLS}>Name</th>
                    <th className={cn(TH_R_CLS, 'w-28')}>Revenue</th>
                    <th className={cn(TH_R_CLS, 'w-28')}>Cost of Sales</th>
                    <th className={cn(TH_R_CLS, 'w-28')}>Gross Profit</th>
                    <th className={cn(TH_R_CLS, 'w-20')}>GM%</th>
                    <th className={cn(TH_R_CLS, 'w-32')}>Operating Expenses</th>
                    <th className={cn(TH_R_CLS, 'w-28')}>Operating Profit</th>
                    <th className={cn(TH_R_CLS, 'w-28')}>Net Profit</th>
                    <th className={cn(TH_R_CLS, 'w-20')}>NM%</th>
                    <th className={cn(TH_R_CLS, 'w-28')}>Previous Net Profit</th>
                    <th className={cn(TH_R_CLS, 'w-24')}>Growth%</th>
                    <th className={cn(TH_R_CLS, 'w-24')}>Contribution%</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.length === 0 ? (
                    <tr>
                      <td colSpan={13} className="px-3 py-8 text-center text-sm text-gray-400 dark:text-gray-600">
                        No data for the selected criteria.
                      </td>
                    </tr>
                  ) : (
                    data.rows.map((r, i) => (
                      <tr
                        key={r.groupId ?? `${r.groupCode}-${i}`}
                        className="border-t border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50"
                      >
                        <td className="px-3 py-2 text-sm font-mono text-gray-600 dark:text-gray-400">{r.groupCode}</td>
                        <td className="px-3 py-2 text-sm text-gray-900 dark:text-gray-100">{r.groupName}</td>
                        <td className={cn(AMT_CLS, 'text-gray-700 dark:text-gray-300')}>{fmtAmt(r.revenue)}</td>
                        <td className={cn(AMT_CLS, 'text-gray-700 dark:text-gray-300')}>{fmtAmt(r.costOfSales)}</td>
                        <td className={cn(AMT_CLS, 'text-gray-700 dark:text-gray-300')}>{fmtAmt(r.grossProfit)}</td>
                        <td className={cn(AMT_CLS, 'text-gray-500 dark:text-gray-400')}>{fmtPct(r.grossMarginPercent)}</td>
                        <td className={cn(AMT_CLS, 'text-gray-700 dark:text-gray-300')}>{fmtAmt(r.operatingExpenses)}</td>
                        <td className={cn(AMT_CLS, 'text-gray-700 dark:text-gray-300')}>{fmtAmt(r.operatingProfit)}</td>
                        <td className={cn(
                          AMT_CLS, 'font-medium',
                          r.netProfit < 0
                            ? 'text-red-600 dark:text-red-400'
                            : 'text-gray-900 dark:text-gray-100',
                        )}>
                          {fmtAmt(r.netProfit)}
                        </td>
                        <td className={cn(AMT_CLS, 'text-gray-500 dark:text-gray-400')}>{fmtPct(r.netMarginPercent)}</td>
                        <td className={cn(AMT_CLS, 'text-gray-500 dark:text-gray-400')}>{fmtAmt(r.previousNetProfit)}</td>
                        <td className="px-3 py-2 text-right">
                          <Delta percent={r.growthPercent} />
                        </td>
                        <td className={cn(AMT_CLS, 'text-gray-500 dark:text-gray-400')}>{fmtPct(r.contributionPercent)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/50">
                    <td colSpan={2} className="px-3 py-2 text-sm font-bold text-gray-900 dark:text-white">TOTAL</td>
                    <td className={cn(AMT_CLS, 'font-bold text-gray-900 dark:text-white')}>{fmtAmt(data.total.revenue)}</td>
                    <td className={cn(AMT_CLS, 'font-bold text-gray-900 dark:text-white')}>{fmtAmt(data.total.costOfSales)}</td>
                    <td className={cn(AMT_CLS, 'font-bold text-gray-900 dark:text-white')}>{fmtAmt(data.total.grossProfit)}</td>
                    <td className={cn(AMT_CLS, 'font-bold text-gray-900 dark:text-white')}>{fmtPct(data.total.grossMarginPercent)}</td>
                    <td className={cn(AMT_CLS, 'font-bold text-gray-900 dark:text-white')}>{fmtAmt(data.total.operatingExpenses)}</td>
                    <td className={cn(AMT_CLS, 'font-bold text-gray-900 dark:text-white')}>{fmtAmt(data.total.operatingProfit)}</td>
                    <td className={cn(AMT_CLS, 'font-bold text-gray-900 dark:text-white')}>{fmtAmt(data.total.netProfit)}</td>
                    <td className={cn(AMT_CLS, 'font-bold text-gray-900 dark:text-white')}>{fmtPct(data.total.netMarginPercent)}</td>
                    <td className={cn(AMT_CLS, 'font-bold text-gray-900 dark:text-white')}>{fmtAmt(data.total.previousNetProfit)}</td>
                    <td className="px-3 py-2 text-right">
                      <Delta percent={data.total.growthPercent} />
                    </td>
                    <td className={cn(AMT_CLS, 'font-bold text-gray-900 dark:text-white')}>{fmtPct(data.total.contributionPercent)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* Waterfall */}
          <Section
            title="Profit Waterfall"
            subtitle={`Revenue to net profit — ${fmtDate(data.fromDate)} to ${fmtDate(data.toDate)}`}
          >
            {data.waterfall.length === 0 ? (
              <EmptyState message="No waterfall data for this period." />
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={data.waterfall} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200 dark:stroke-gray-700" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} interval={0} height={50} angle={-15} textAnchor="end" />
                  <YAxis tick={{ fontSize: 11 }} width={80} />
                  <ChartTooltip content={<AmountTooltip />} />
                  <Bar dataKey="amount" name="Amount" radius={[3, 3, 0, 0]}>
                    {data.waterfall.map((step, i) => (
                      <Cell
                        key={`${step.label}-${i}`}
                        fill={
                          step.isTotal
                            ? TOTAL_BAR_COLOUR
                            : step.amount < 0
                              ? NEGATIVE_BAR_COLOUR
                              : POSITIVE_BAR_COLOUR
                        }
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </Section>

          {/* Margin trend + contribution */}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <Section title="Margin Trend" subtitle="Revenue, gross profit and net profit by period">
              {data.marginTrend.length === 0 ? (
                <EmptyState message="No trend data for this period." />
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <LineChart data={data.marginTrend} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200 dark:stroke-gray-700" />
                    <XAxis dataKey="period" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} width={80} />
                    <ChartTooltip content={<AmountTooltip />} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Line type="monotone" dataKey="revenue" name="Revenue" stroke={CHART_COLOURS[0]} strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="grossProfit" name="Gross Profit" stroke={CHART_COLOURS[1]} strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="netProfit" name="Net Profit" stroke={CHART_COLOURS[4]} strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </Section>

            <Section title="Net Profit Contribution" subtitle={`Top ${contributionData.length} by ${groupLabel.toLowerCase()}`}>
              {contributionData.length === 0 ? (
                <EmptyState message="No contribution data for this period." />
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={contributionData} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200 dark:stroke-gray-700" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} height={50} angle={-15} textAnchor="end" />
                    <YAxis tick={{ fontSize: 11 }} width={80} />
                    <ChartTooltip content={<AmountTooltip />} />
                    <Bar dataKey="netProfit" name="Net Profit" radius={[3, 3, 0, 0]}>
                      {contributionData.map((d, i) => (
                        <Cell
                          key={`${d.name}-${i}`}
                          fill={d.netProfit < 0 ? NEGATIVE_BAR_COLOUR : CHART_COLOURS[i % CHART_COLOURS.length]}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Section>
          </div>
        </div>
      )}

      {/* Print preview */}
      {data && (
        <ReportPrintPreview
          isOpen={showPrint}
          onClose={() => setShowPrint(false)}
          title="Profitability Analysis"
          orientation="landscape"
        >
          <ReportHeader
            reportTitle="Profitability Analysis"
            printedBy={user?.fullName}
            printedAt={new Date()}
            meta={[
              { label: 'Company', value: data.companyName },
              { label: 'Period', value: `${fmtDate(data.fromDate)} to ${fmtDate(data.toDate)}` },
              { label: 'Compared With', value: `${fmtDate(data.previousFromDate)} to ${fmtDate(data.previousToDate)}` },
              { label: 'Grouped By', value: GROUP_BY_LABELS[data.groupBy] },
            ]}
          />
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '8.5px', marginTop: '8px' }}>
            <thead>
              <tr>
                {['Code', 'Name', 'Revenue', 'Cost of Sales', 'Gross Profit', 'GM%', 'Operating Exp.', 'Operating Profit', 'Net Profit', 'NM%', 'Prev. Net Profit', 'Contribution%'].map((h, i) => (
                  <th
                    key={h}
                    style={{
                      background: '#1e3a5f', color: 'white', padding: '5px 6px',
                      textAlign: i >= 2 ? 'right' : 'left', fontWeight: 700,
                      whiteSpace: 'nowrap', fontSize: '8.5px',
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r, i) => (
                <tr key={r.groupId ?? `${r.groupCode}-${i}`} style={{ borderBottom: '1px solid #f3f4f6' }}>
                  <td style={{ padding: '3px 6px', fontFamily: 'monospace', color: '#6b7280' }}>{r.groupCode}</td>
                  <td style={{ padding: '3px 6px' }}>{r.groupName}</td>
                  {[r.revenue, r.costOfSales, r.grossProfit].map((v, idx) => (
                    <td key={idx} style={{ padding: '3px 6px', textAlign: 'right', fontFamily: 'monospace' }}>{fmtAmt(v)}</td>
                  ))}
                  <td style={{ padding: '3px 6px', textAlign: 'right' }}>{fmtPct(r.grossMarginPercent)}</td>
                  {[r.operatingExpenses, r.operatingProfit, r.netProfit].map((v, idx) => (
                    <td key={idx} style={{ padding: '3px 6px', textAlign: 'right', fontFamily: 'monospace', fontWeight: idx === 2 ? 600 : 400 }}>{fmtAmt(v)}</td>
                  ))}
                  <td style={{ padding: '3px 6px', textAlign: 'right' }}>{fmtPct(r.netMarginPercent)}</td>
                  <td style={{ padding: '3px 6px', textAlign: 'right', fontFamily: 'monospace' }}>{fmtAmt(r.previousNetProfit)}</td>
                  <td style={{ padding: '3px 6px', textAlign: 'right' }}>{fmtPct(r.contributionPercent)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ borderTop: '2px solid #1e3a5f', background: '#f9fafb' }}>
                <td colSpan={2} style={{ padding: '4px 6px', fontWeight: 700 }}>TOTAL</td>
                {[data.total.revenue, data.total.costOfSales, data.total.grossProfit].map((v, idx) => (
                  <td key={idx} style={{ padding: '4px 6px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 700 }}>{fmtAmt(v)}</td>
                ))}
                <td style={{ padding: '4px 6px', textAlign: 'right', fontWeight: 700 }}>{fmtPct(data.total.grossMarginPercent)}</td>
                {[data.total.operatingExpenses, data.total.operatingProfit, data.total.netProfit].map((v, idx) => (
                  <td key={idx} style={{ padding: '4px 6px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 700 }}>{fmtAmt(v)}</td>
                ))}
                <td style={{ padding: '4px 6px', textAlign: 'right', fontWeight: 700 }}>{fmtPct(data.total.netMarginPercent)}</td>
                <td style={{ padding: '4px 6px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 700 }}>{fmtAmt(data.total.previousNetProfit)}</td>
                <td style={{ padding: '4px 6px', textAlign: 'right', fontWeight: 700 }}>{fmtPct(data.total.contributionPercent)}</td>
              </tr>
            </tfoot>
          </table>
          <ReportFooter />
        </ReportPrintPreview>
      )}
    </div>
  );
};
